import React, { useState, useEffect } from 'react';
import { useAdmin } from '../contexts/AdminContext';
import { adminAPI, skinAPI } from '../services/api';
import toast from 'react-hot-toast';
import QuickLogin from '../components/QuickLogin';
import AdminNavigation from '../components/AdminNavigation';
import { 
  Trophy,
  Calendar,
  MapPin,
  Download,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Users,
  Target,
  Clock,
  Award
} from 'lucide-react';

const MatchResultsAdminPage = () => {
  const { isAuthenticated } = useAdmin();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [statistics, setStatistics] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [matchDetails, setMatchDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [skinDatabase, setSkinDatabase] = useState(null);

  // Component for handling async skin image loading
  const EnhancedSkinImage = ({ skin }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadImage = async () => {
        try {
          setLoading(true);
          const imageUrl = await generateSkinImageUrl(
            skin.weapon, 
            skin.skin_name, 
            skin.id // Use the demo skin surrogate ID
          );
          setImageSrc(imageUrl);
        } catch (error) {
          console.error('Failed to load skin image:', error);
          setImageSrc(`https://placehold.co/80x60/1a1a2e/ffffff/png?text=${encodeURIComponent(skin.weapon)}`);
        } finally {
          setLoading(false);
        }
      };

      loadImage();
    }, [skin.weapon, skin.skin_name, skin.id]);

    if (loading) {
      return (
        <div className="max-h-20 max-w-full flex items-center justify-center bg-gray-800 rounded">
          <div className="animate-pulse text-gray-400 text-xs">Loading...</div>
        </div>
      );
    }

    return (
      <img
        src={imageSrc}
        alt={`${skin.weapon} | ${skin.skin_name}`}
        className="max-h-20 max-w-full object-contain"
        onError={(e) => {
          e.target.src = `https://placehold.co/80x60/1a1a2e/ffffff/png?text=${encodeURIComponent(skin.weapon)}`;
        }}
      />
    );
  };

  // Function to fetch skin database from ByMykel's API
  const fetchSkinDatabase = async () => {
    if (skinDatabase) return skinDatabase; // Return cached version
    
    try {
      console.log('🔄 Fetching skin database from ByMykel API...');
      const response = await fetch('https://raw.githubusercontent.com/ByMykel/CSGO-API/main/public/api/en/skins.json');
      const skins = await response.json();
      
      // Create a lookup map for faster searches
      const skinMap = {};
      skins.forEach(skin => {
        const key = `${skin.weapon.name}|${skin.name.split(' | ')[1] || skin.name}`;
        skinMap[key] = skin;
      });
      
      console.log(`✅ Loaded ${skins.length} skins from ByMykel API`);
      setSkinDatabase(skinMap);
      return skinMap;
    } catch (error) {
      console.error('❌ Failed to fetch skin database:', error);
      return null;
    }
  };

  // Enhanced function to generate skin image URLs using our mapping system + fallback
  const generateSkinImageUrl = async (weapon, skinName, demoSkinId = null) => {
    // Handle vanilla skins first
    if (skinName === 'Vanilla') {
      const weaponMap = {
        'AK-47': 'weapon_ak47',
        'M4A4': 'weapon_m4a1',
        'M4A1-S': 'weapon_m4a1_silencer',
        'AWP': 'weapon_awp',
        'Desert Eagle': 'weapon_deagle',
        'Glock-18': 'weapon_glock',
        'USP-S': 'weapon_usp_silencer',
        'P250': 'weapon_p250',
        'FAMAS': 'weapon_famas',
        'Galil AR': 'weapon_galilar',
        'MP9': 'weapon_mp9',
        'MAC-10': 'weapon_mac10',
        'UMP-45': 'weapon_ump45',
        'P90': 'weapon_p90',
        'PP-Bizon': 'weapon_bizon',
        'XM1014': 'weapon_xm1014',
        'Nova': 'weapon_nova',
        'MAG-7': 'weapon_mag7',
        'Negev': 'weapon_negev',
        'M249': 'weapon_m249',
        'Five-SeveN': 'weapon_fiveseven',
        'Tec-9': 'weapon_tec9',
        'CZ75-Auto': 'weapon_cz75a',
        'Dual Berettas': 'weapon_elite',
        'P2000': 'weapon_hkp2000',
        'MP7': 'weapon_mp7',
        'MP5-SD': 'weapon_mp5sd',
        'Sawed-Off': 'weapon_sawedoff',
        'SG 553': 'weapon_sg556',
        'AUG': 'weapon_aug',
        'SSG 08': 'weapon_ssg08',
        'SCAR-20': 'weapon_scar20',
        'G3SG1': 'weapon_g3sg1',
        'Knife': 'weapon_knife',
        'Karambit': 'weapon_knife_karambit',
        'Bayonet': 'weapon_knife_bayonet',
        'M9 Bayonet': 'weapon_knife_m9_bayonet',
        'Butterfly Knife': 'weapon_knife_butterfly',
        'Huntsman Knife': 'weapon_knife_tactical',
        'Flip Knife': 'weapon_knife_flip',
        'Gut Knife': 'weapon_knife_gut',
        'Falchion Knife': 'weapon_knife_falchion',
        'Bowie Knife': 'weapon_knife_survival_bowie',
        'Shadow Daggers': 'weapon_knife_push',
        'Navaja Knife': 'weapon_knife_gypsy_jackknife',
        'Stiletto Knife': 'weapon_knife_stiletto',
        'Talon Knife': 'weapon_knife_widowmaker',
        'Ursus Knife': 'weapon_knife_ursus',
        'Skeleton Knife': 'weapon_knife_skeleton',
        'Survival Knife': 'weapon_knife_canis',
        'Nomad Knife': 'weapon_knife_outdoor',
        'Paracord Knife': 'weapon_knife_cord',
        'Classic Knife': 'weapon_knife_css'
      };
      
      const weaponSlug = weaponMap[weapon] || 'weapon_knife';
      return `https://raw.githubusercontent.com/ByMykel/counter-strike-image-tracker/main/static/panorama/images/econ/default_generated/${weaponSlug}_light_png.png`;
    }

    // Try our new mapping-based resolution system first (most reliable)
    try {
      const response = await skinAPI.resolveSkinImage(weapon, skinName, demoSkinId);
      if (response.data.success && response.data.data.image_url) {
        console.log(`✅ Found image via ${response.data.data.source} for ${weapon} | ${skinName}: ${response.data.data.image_url}`);
        return response.data.data.image_url;
      }
    } catch (error) {
      console.log(`⚠️ Mapping resolution failed for ${weapon} | ${skinName}:`, error);
    }

    // Fallback 1: Try to find exact match in the old ByMykel skin database
    if (skinDatabase) {
      const key = `${weapon}|${skinName}`;
      const skin = skinDatabase[key];
      
      if (skin && skin.image) {
        console.log(`✅ Found exact match via old method for ${weapon} | ${skinName}: ${skin.image}`);
        return skin.image;
      }
    }

    // Fallback 2: Generic pattern generation
    const weaponMap = {
      'AK-47': 'weapon_ak47',
      'M4A4': 'weapon_m4a1',
      'M4A1-S': 'weapon_m4a1_silencer',
      'AWP': 'weapon_awp',
      'Desert Eagle': 'weapon_deagle',
      'Glock-18': 'weapon_glock',
      'USP-S': 'weapon_usp_silencer',
      'P250': 'weapon_p250',
      'FAMAS': 'weapon_famas',
      'Galil AR': 'weapon_galilar',
      'MP9': 'weapon_mp9',
      'MAC-10': 'weapon_mac10',
      'UMP-45': 'weapon_ump45',
      'P90': 'weapon_p90',
      'PP-Bizon': 'weapon_bizon',
      'XM1014': 'weapon_xm1014',
      'Nova': 'weapon_nova',
      'MAG-7': 'weapon_mag7',
      'Negev': 'weapon_negev',
      'M249': 'weapon_m249',
      'Five-SeveN': 'weapon_fiveseven',
      'Tec-9': 'weapon_tec9',
      'CZ75-Auto': 'weapon_cz75a',
      'Dual Berettas': 'weapon_elite',
      'P2000': 'weapon_hkp2000',
      'MP7': 'weapon_mp7',
      'MP5-SD': 'weapon_mp5sd',
      'Sawed-Off': 'weapon_sawedoff',
      'SG 553': 'weapon_sg556',
      'AUG': 'weapon_aug',
      'SSG 08': 'weapon_ssg08',
      'SCAR-20': 'weapon_scar20',
      'G3SG1': 'weapon_g3sg1',
      'Knife': 'weapon_knife',
      'Karambit': 'weapon_knife_karambit',
      'Bayonet': 'weapon_knife_bayonet',
      'M9 Bayonet': 'weapon_knife_m9_bayonet',
      'Butterfly Knife': 'weapon_knife_butterfly',
      'Huntsman Knife': 'weapon_knife_tactical',
      'Flip Knife': 'weapon_knife_flip',
      'Gut Knife': 'weapon_knife_gut',
      'Falchion Knife': 'weapon_knife_falchion',
      'Bowie Knife': 'weapon_knife_survival_bowie',
      'Shadow Daggers': 'weapon_knife_push',
      'Navaja Knife': 'weapon_knife_gypsy_jackknife',
      'Stiletto Knife': 'weapon_knife_stiletto',
      'Talon Knife': 'weapon_knife_widowmaker',
      'Ursus Knife': 'weapon_knife_ursus',
      'Skeleton Knife': 'weapon_knife_skeleton',
      'Survival Knife': 'weapon_knife_canis',
      'Nomad Knife': 'weapon_knife_outdoor',
      'Paracord Knife': 'weapon_knife_cord',
      'Classic Knife': 'weapon_knife_css'
    };

    const weaponSlug = weaponMap[weapon] || 'weapon_knife';
    const skinSlug = skinName.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
    
    const fallbackUrl = `https://raw.githubusercontent.com/ByMykel/counter-strike-image-tracker/main/static/panorama/images/econ/default_generated/${weaponSlug}_${skinSlug}_light_png.png`;
    console.log(`⚠️ Using fallback pattern for ${weapon} | ${skinName}: ${fallbackUrl}`);
    
    return fallbackUrl;
  };

  // Helper function to get rarity colors
  const getRarityColor = (quality) => {
    const rarityColors = {
      'confirmed': { border: 'border-green-500', text: 'text-green-400' },
      'highest': { border: 'border-purple-500', text: 'text-purple-400' },
      'professional': { border: 'border-blue-500', text: 'text-blue-400' },
      'high': { border: 'border-yellow-500', text: 'text-yellow-400' },
      'medium': { border: 'border-orange-500', text: 'text-orange-400' },
      'low': { border: 'border-gray-500', text: 'text-gray-400' }
    };

    return rarityColors[quality] || rarityColors['medium'];
  };

  const loadMatches = async (page = 1) => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await adminAPI.getHltvMatches(page, 100);
      setMatches(response.data.data || []);
      setPagination(response.data.pagination || {});
      setStatistics(response.data.statistics || {});
      setCurrentPage(page);
    } catch (error) {
      toast.error('Failed to load match results');
      console.error('Error loading matches:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches(1);
    fetchSkinDatabase(); // Load skin database on component mount
  }, [isAuthenticated]);

  const handlePageChange = (page) => {
    loadMatches(page);
  };

  const toggleMatchDetails = async (match) => {
    const matchId = match.id;
    if (expandedMatch === matchId) {
      setExpandedMatch(null);
      return;
    }

    setExpandedMatch(matchId);
    
    // Load match details if not already loaded
    if (!matchDetails[matchId]) {
      await loadMatchDetails(match);
    }
  };

  const loadMatchDetails = async (match) => {
    const matchId = match.id;
    const hltvMatchId = match.hltv_match_id;
    setLoadingDetails(prev => ({ ...prev, [matchId]: true }));
    
    try {
      // Load demo files for this match using HLTV match ID
      const response = await adminAPI.getDemoFiles(1, 100, 'completed', hltvMatchId);
      const demoFiles = response.data.data || [];

      // Load skins for each demo file
      const demosWithSkins = [];
      for (const demo of demoFiles) {
        try {
          const skinsResponse = await adminAPI.getDemoSkins(demo.id);
          if (skinsResponse.data.success) {
            const skins = skinsResponse.data.data.skins || [];
            demosWithSkins.push({
              ...demo,
              skins: skins
            });
          }
        } catch (error) {
          console.error(`Error loading skins for demo ${demo.id}:`, error);
          demosWithSkins.push({
            ...demo,
            skins: []
          });
        }
      }

      const totalSkins = demosWithSkins.reduce((sum, demo) => sum + (demo.skins?.length || 0), 0);
      
      setMatchDetails(prev => ({
        ...prev,
        [matchId]: {
          demoFiles: demosWithSkins,
          totalSkins: totalSkins,
          totalDemos: demosWithSkins.length
        }
      }));

    } catch (error) {
      console.error('Error loading match details:', error);
      toast.error('Failed to load match details');
    } finally {
      setLoadingDetails(prev => ({ ...prev, [matchId]: false }));
    }
  };

  const getWinnerBadge = (match) => {
    if (!match.winner) return null;
    
    const isTeamAWinner = match.winner.id === match.team_a?.id;
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        isTeamAWinner ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
      }`}>
        <Trophy className="w-3 h-3 mr-1" />
        Winner: {match.winner.name}
      </div>
    );
  };

  const getTeamLogo = (team) => {
    if (!team?.logo_url) return null;
    return (
      <img 
        src={team.logo_url} 
        alt={team.name}
        className="w-8 h-8 object-contain"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const renderPagination = () => {
    if (!pagination.pages || pagination.pages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

    // Previous button
    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          className="px-3 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md"
        >
          Previous
        </button>
      );
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 border rounded-md ${
            i === currentPage
              ? 'bg-red-600 text-white border-red-600'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }

    // Next button
    if (currentPage < pagination.pages) {
      pages.push(
        <button
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          className="px-3 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md"
        >
          Next
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center space-x-2 mt-6">
        {pages}
      </div>
    );
  };

  if (!isAuthenticated) {
    return <QuickLogin />;
  }

  return (
    <>
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="min-h-screen bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-red-400 mb-2">HLTV Match Results</h1>
          <p className="text-gray-300">Manage and view detailed match results from HLTV crawler</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-red-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Total Matches</p>
                <p className="text-2xl font-bold text-white">{statistics.total_matches || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center">
              <Download className="h-8 w-8 text-green-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Demos Downloaded</p>
                <p className="text-2xl font-bold text-white">{statistics.demos_downloaded || 0}</p>
                <p className="text-xs text-gray-500">{statistics.matches_with_demos || 0} available</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-blue-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Demos Processed</p>
                <p className="text-2xl font-bold text-white">{statistics.processed_demo_files || 0}</p>
                <p className="text-xs text-gray-500">{statistics.total_demo_files || 0} total files</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center">
              <Award className="h-8 w-8 text-purple-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Skins Extracted</p>
                <p className="text-2xl font-bold text-white">{statistics.total_skins_extracted || 0}</p>
                <p className="text-xs text-gray-500">from all demos</p>
              </div>
            </div>
          </div>
        </div>

        {/* Matches List */}
        <div className="bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Match Results</h2>
              <span className="text-sm text-gray-400">
                Showing {matches.length} of {pagination.total || 0} matches
              </span>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-400"></div>
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12">
              <Target className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-300">No matches found</h3>
              <p className="mt-1 text-sm text-gray-500">Start crawling HLTV results to see matches here.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {matches.map((match) => (
                <div key={match.id} className="p-6">
                  {/* Match Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      {/* Team A */}
                      <div className="flex items-center space-x-2">
                        {getTeamLogo(match.team_a)}
                        <span className="font-medium text-white">
                          {match.team_a?.name || 'Unknown Team'}
                        </span>
                        {match.team_a?.current_rank && (
                          <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                            #{match.team_a.current_rank}
                          </span>
                        )}
                      </div>

                      {/* Score */}
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-red-400">
                          {match.formatted_score}
                        </span>
                        <span className="text-gray-400">vs</span>
                      </div>

                      {/* Team B */}
                      <div className="flex items-center space-x-2">
                        {getTeamLogo(match.team_b)}
                        <span className="font-medium text-white">
                          {match.team_b?.name || 'Unknown Team'}
                        </span>
                        {match.team_b?.current_rank && (
                          <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                            #{match.team_b.current_rank}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      {/* Demo Status Indicators */}
                      <div className="flex items-center space-x-2">
                        {/* Demo Downloaded Status */}
                        {match.demo_download_status === 'completed' ? (
                          <div className="flex items-center space-x-1 bg-green-600 text-white px-2 py-1 rounded-full text-xs">
                            <Download className="w-3 h-3" />
                            <span>Downloaded</span>
                          </div>
                        ) : match.has_demo ? (
                          <div className="flex items-center space-x-1 bg-gray-600 text-gray-300 px-2 py-1 rounded-full text-xs">
                            <Download className="w-3 h-3" />
                            <span>Available</span>
                          </div>
                        ) : null}
                        
                        {/* Demo Processing Status */}
                        {match.processed_demos_count > 0 && (
                          <div className="flex items-center space-x-1 bg-blue-600 text-white px-2 py-1 rounded-full text-xs">
                            <Target className="w-3 h-3" />
                            <span>{match.processed_demos_count} Processed</span>
                          </div>
                        )}
                        
                        {/* Skins Extracted Count */}
                        {match.total_skins_extracted > 0 && (
                          <div className="flex items-center space-x-1 bg-purple-600 text-white px-2 py-1 rounded-full text-xs">
                            <Award className="w-3 h-3" />
                            <span>{match.total_skins_extracted} Skins</span>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => toggleMatchDetails(match)}
                        className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
                      >
                        <span>Details</span>
                        {expandedMatch === match.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Quick Info */}
                  <div className="flex items-center space-x-6 text-sm text-gray-400 mb-4">
                    {match.tournament && (
                      <div className="flex items-center">
                        <Award className="w-4 h-4 mr-1" />
                        <span>{match.tournament.name}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>{formatDate(match.match_date)}</span>
                    </div>

                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      <span>BO{match.best_of}</span>
                    </div>

                    {match.has_demo && (
                      <div className="flex items-center text-green-400">
                        <Download className="w-4 h-4 mr-1" />
                        <span>Demo Available</span>
                      </div>
                    )}
                  </div>

                  {/* Winner Badge */}
                  <div className="mb-4">
                    {getWinnerBadge(match)}
                  </div>

                  {/* Expanded Details */}
                  {expandedMatch === match.id && (
                    <div className="bg-gray-900 rounded-lg p-4 mt-4">
                      {loadingDetails[match.id] ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                          <span className="ml-3 text-gray-400">Loading match details...</span>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Match Details */}
                            <div>
                              <h4 className="text-lg font-medium text-white mb-3">Match Details</h4>
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-400">HLTV Match ID:</span>
                                  <span className="text-white">{match.hltv_match_id}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Status:</span>
                                  <span className={`capitalize ${
                                    match.is_completed ? 'text-green-400' : 'text-yellow-400'
                                  }`}>
                                    {match.status}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Best of:</span>
                                  <span className="text-white">BO{match.best_of}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Significance:</span>
                                  <span className="text-white">{match.significance}/10</span>
                                </div>
                                {matchDetails[match.id] && (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-gray-400">Demo Files:</span>
                                      <span className="text-white">{matchDetails[match.id].totalDemos}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-400">Total Skins:</span>
                                      <span className="text-white">{matchDetails[match.id].totalSkins}</span>
                                    </div>
                                  </>
                                )}
                                {match.tournament && (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-gray-400">Tournament:</span>
                                      <span className="text-white">{match.tournament.name}</span>
                                    </div>
                                    {match.tournament.organizer && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-400">Organizer:</span>
                                        <span className="text-white">{match.tournament.organizer}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Maps & Links */}
                            <div>
                              <h4 className="text-lg font-medium text-white mb-3">Maps & Links</h4>
                              
                              {/* Maps */}
                              {match.maps.maps_array.length > 0 && (
                                <div className="mb-4">
                                  <div className="text-sm text-gray-400 mb-2">Maps Played:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {match.maps.maps_array.map((map, index) => (
                                      <span
                                        key={index}
                                        className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-sm"
                                      >
                                        {map}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Links */}
                              <div className="space-y-2">
                                {match.match_url && (
                                  <a
                                    href={match.match_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    View on HLTV
                                  </a>
                                )}
                                {match.demo_url && (
                                  <a
                                    href={match.demo_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center text-green-400 hover:text-green-300 transition-colors"
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download Demo
                                  </a>
                                )}
                              </div>

                              {/* Timestamps */}
                              <div className="mt-4 text-xs text-gray-500">
                                <div>Created: {formatDate(match.created_at)}</div>
                                <div>Updated: {formatDate(match.updated_at)}</div>
                              </div>
                            </div>
                          </div>

                          {/* Demo Files Section */}
                          {matchDetails[match.id]?.demoFiles && (
                            <div>
                              <h4 className="text-lg font-medium text-white mb-3">Demo Files</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {matchDetails[match.id].demoFiles.map((demo) => (
                                  <div key={demo.id} className="bg-gray-800 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-white font-medium text-sm truncate">{demo.filename}</span>
                                      <span className="text-green-400 text-xs">{demo.skins?.length || 0} skins</span>
                                    </div>
                                    <div className="text-xs text-gray-400 space-y-1">
                                      <div>Map: {demo.map_name || 'Unknown'}</div>
                                      <div>Players: {demo.players_found || 0}</div>
                                      <div>Size: {demo.file_size ? `${(demo.file_size / 1024 / 1024).toFixed(1)} MB` : 'Unknown'}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Skins Section */}
                          {matchDetails[match.id]?.demoFiles && matchDetails[match.id].totalSkins > 0 && (
                            <div>
                              <h4 className="text-lg font-medium text-white mb-3">Extracted Skins ({matchDetails[match.id].totalSkins} total)</h4>
                              <div className="space-y-4">
                                {matchDetails[match.id].demoFiles.map((demo) => (
                                  demo.skins && demo.skins.length > 0 && (
                                    <div key={demo.id} className="bg-gray-800 rounded-lg p-4">
                                      <h5 className="text-white font-medium mb-3 flex items-center">
                                        <MapPin className="w-4 h-4 mr-2 text-blue-400" />
                                        {demo.filename} ({demo.skins.length} skins)
                                      </h5>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 max-h-96 overflow-y-auto">
                                        {demo.skins.slice(0, 100).map((skin, skinIndex) => {
                                          const rarityColor = getRarityColor(skin.data_quality);
                                          
                                          return (
                                            <div key={skinIndex} className={`bg-gray-700 rounded-lg overflow-hidden hover:bg-gray-600 transition-colors border-l-4 ${rarityColor.border}`}>
                                              {/* Skin Image */}
                                              <div className="relative h-24 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                                <EnhancedSkinImage skin={skin} />
                                              </div>
                                              
                                              {/* Skin Info */}
                                              <div className="p-3">
                                                <div className="flex items-center justify-between mb-2">
                                                  <span className="text-white font-medium text-sm truncate">{skin.player_name}</span>
                                                  <span className="text-xs text-gray-400 truncate">{skin.team}</span>
                                                </div>
                                                <div className="text-blue-400 font-medium mb-2 text-sm truncate">
                                                  {skin.weapon} | {skin.skin_name}
                                                </div>
                                                <div className="text-xs text-gray-400 space-y-1">
                                                  <div className="flex items-center justify-between">
                                                    <span>Condition:</span>
                                                    <span className="text-white">{skin.wear_name || 'Unknown'}</span>
                                                  </div>
                                                  <div className="flex items-center justify-between">
                                                    <span>Quality:</span>
                                                    <span className={`${rarityColor.text} font-medium`}>{skin.data_quality}</span>
                                                  </div>
                                                  {skin.custom_name && (
                                                    <div className="text-yellow-400 text-xs truncate mt-2 italic">
                                                      "{skin.custom_name}"
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      {demo.skins.length > 100 && (
                                        <div className="mt-3 text-center text-gray-400 text-sm">
                                          Showing 100 of {demo.skins.length} skins
                                        </div>
                                      )}
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {renderPagination()}
        </div>
      </div>
        </div>
      </div>
    </>
  );
};

export default MatchResultsAdminPage;
